import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wrench, ChevronLeft, ChevronRight, Upload, X, Building2, AlertTriangle } from "lucide-react";

const CATEGORIES = [
  { value: "electrico", label: "Electrico", icon: "⚡" },
  { value: "plomeria", label: "Plomeria", icon: "🔧" },
  { value: "refrigeracion", label: "Refrigeracion", icon: "❄" },
  { value: "cocina", label: "Cocina", icon: "🍳" },
  { value: "limpieza_industrial", label: "Limpieza industrial", icon: "🧹" },
  { value: "estructural", label: "Estructural", icon: "🏗" },
  { value: "tecnologia", label: "Tecnologia", icon: "💻" },
  { value: "fumigacion", label: "Fumigacion", icon: "🌿" },
  { value: "otros", label: "Otros", icon: "🔩" },
];

const PRIORITIES = [
  { value: "baja", label: "Baja", color: "border-slate-300 text-slate-600 bg-slate-50" },
  { value: "media", label: "Media", color: "border-blue-300 text-blue-700 bg-blue-50" },
  { value: "alta", label: "Alta", color: "border-orange-300 text-orange-700 bg-orange-50" },
  { value: "urgente", label: "Urgente", color: "border-red-300 text-red-700 bg-red-50" },
];

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function OpsMantenimientoNuevo() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [branches, setBranches] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    branch_id: "",
    category: "",
    priority: "",
    title: "",
    description: "",
    estimated_cost_mxn: "",
    photos: [],           // { file, preview, base64 }
  });

  useEffect(() => {
    api.get("/branches").then(r => {
      setBranches(r.data || []);
      // Pre-seleccionar sucursal si branch_manager de una sola
      if (user?.role === "branch_manager") {
        const authorized = user.branches_authorized || [];
        if (authorized.length === 1) {
          const branch = (r.data || []).find(b => b.code === authorized[0]);
          if (branch) setForm(f => ({ ...f, branch_id: branch.id }));
        }
      }
      // Fix WH13 (2026-08-30): operativo queda fijo en su propia sucursal
      if (user?.role === "operativo" && user?.branch_id) {
        const branch = (r.data || []).find(b => b.id === user.branch_id);
        if (branch) setForm(f => ({ ...f, branch_id: branch.id }));
      }
    }).catch(() => {});
  }, [user]);

  const canNextStep1 = form.branch_id && form.category && form.priority;
  const canNextStep2 = form.title.trim().length >= 3 && form.description.trim().length >= 10;
  const canSubmit = form.photos.length >= 1;

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files || []);
    if (form.photos.length + files.length > 5) {
      toast.error("Maximo 5 fotos por solicitud");
      return;
    }
    const newPhotos = await Promise.all(
      files.map(async file => {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} supera los 5 MB`);
          return null;
        }
        const b64 = await toBase64(file);
        return { file, preview: URL.createObjectURL(file), base64: b64, name: file.name };
      })
    );
    setForm(f => ({ ...f, photos: [...f.photos, ...newPhotos.filter(Boolean)] }));
    e.target.value = "";
  }

  function removePhoto(idx) {
    setForm(f => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        branch_id: form.branch_id,
        category: form.category,
        priority: form.priority,
        title: form.title.trim(),
        description: form.description.trim(),
        estimated_cost_mxn: form.estimated_cost_mxn ? parseFloat(form.estimated_cost_mxn) : null,
        problem_photos: form.photos.map(p => p.base64),
      };
      const res = await api.post("/ops/maintenance/requests", payload);
      toast.success(`Solicitud ${res.data.request_number} creada`);
      navigate(`/ops/mantenimientos/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear la solicitud");
    } finally {
      setSubmitting(false);
    }
  }

  const visibleBranches = user?.role === "branch_manager"
    ? branches.filter(b => (user.branches_authorized || []).includes(b.code))
    : user?.role === "operativo"
      ? branches.filter(b => b.id === user?.branch_id)
      : branches;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/ops/mantenimientos")} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-yellow-500" />
            Nueva solicitud de mantenimiento
          </h1>
          <p className="text-sm text-slate-500">Paso {step} de 3</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex gap-1 mb-6">
        {[1, 2, 3].map(n => (
          <div
            key={n}
            className={`h-1.5 flex-1 rounded-full transition-colors ${n <= step ? "bg-yellow-400" : "bg-slate-200"}`}
          />
        ))}
      </div>

      {/* Step 1: Identificacion */}
      {step === 1 && (
        <div className="space-y-5" data-testid="mnt-step-1">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Sucursal <span className="text-red-500">*</span>
            </label>
            <select
              data-testid="mnt-branch-select"
              value={form.branch_id}
              onChange={e => setForm(f => ({ ...f, branch_id: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-yellow-300"
            >
              <option value="">Seleccionar sucursal...</option>
              {visibleBranches.map(b => (
                <option key={b.id} value={b.id}>{b.name} ({b.code || b.pos_branch_code || ""})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Categoria <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  data-testid={`mnt-cat-${cat.value}`}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, category: cat.value }))}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-xs font-medium transition-all ${
                    form.category === cat.value
                      ? "border-yellow-400 bg-yellow-50 text-yellow-800"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <span className="text-xl">{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Prioridad <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  data-testid={`mnt-priority-${p.value}`}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, priority: p.value }))}
                  className={`flex-1 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all ${
                    form.priority === p.value ? p.color + " ring-2 ring-offset-1 ring-current" : "border-slate-200 text-slate-500 bg-white hover:bg-slate-50"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <button
            data-testid="mnt-next-step1"
            onClick={() => setStep(2)}
            disabled={!canNextStep1}
            className="w-full py-3 bg-[#FFDF00] hover:bg-yellow-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-900 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            Continuar <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Step 2: Descripcion */}
      {step === 2 && (
        <div className="space-y-5" data-testid="mnt-step-2">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Titulo corto <span className="text-red-500">*</span>
            </label>
            <input
              data-testid="mnt-title-input"
              type="text"
              maxLength={100}
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Ej: Freidora 2 no calienta"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
            />
            <p className="text-xs text-slate-400 mt-1">{form.title.length}/100 caracteres</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Descripcion detallada <span className="text-red-500">*</span>
            </label>
            <textarea
              data-testid="mnt-description-input"
              rows={5}
              minLength={10}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe el problema con el mayor detalle posible: cuando se vio el problema por primera vez, si hubo algun incidente, que parte o equipo esta afectado..."
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Costo estimado (MXN) — opcional
            </label>
            <input
              data-testid="mnt-cost-input"
              type="number"
              min={0}
              value={form.estimated_cost_mxn}
              onChange={e => setForm(f => ({ ...f, estimated_cost_mxn: e.target.value }))}
              placeholder="0.00"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setStep(1)}
              className="flex-1 py-3 border border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
            >
              Atras
            </button>
            <button
              data-testid="mnt-next-step2"
              onClick={() => setStep(3)}
              disabled={!canNextStep2}
              className="flex-1 py-3 bg-[#FFDF00] hover:bg-yellow-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-900 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              Continuar <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Evidencia + envio */}
      {step === 3 && (
        <div className="space-y-5" data-testid="mnt-step-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Fotos del problema <span className="text-red-500">*</span>
              <span className="text-slate-400 font-normal ml-2">(1 a 5, max 5 MB cada una)</span>
            </label>
            {/* Upload zone */}
            <label
              data-testid="mnt-photo-upload"
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl p-6 cursor-pointer hover:border-yellow-400 hover:bg-yellow-50 transition-all"
            >
              <Upload className="w-6 h-6 text-slate-400" />
              <span className="text-sm text-slate-500">Subir foto (JPG, PNG, HEIC, WebP)</span>
              <input
                type="file"
                multiple
                accept="image/*,.heic,.heif"
                className="hidden"
                onChange={handlePhotoUpload}
                disabled={form.photos.length >= 5}
              />
            </label>
            {/* Preview grid */}
            {form.photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {form.photos.map((p, idx) => (
                  <div key={idx} className="relative rounded-lg overflow-hidden border border-slate-200 aspect-square">
                    <img src={p.preview} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!canSubmit && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              Necesitas al menos 1 foto del problema para enviar la solicitud
            </div>
          )}
          {/* Resumen */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-sm space-y-1.5">
            <p className="font-semibold text-slate-700 mb-2">Resumen de la solicitud</p>
            <p><span className="text-slate-500">Sucursal:</span> {branches.find(b => b.id === form.branch_id)?.name || form.branch_id}</p>
            <p><span className="text-slate-500">Categoria:</span> {form.category}</p>
            <p><span className="text-slate-500">Prioridad:</span> {form.priority}</p>
            <p><span className="text-slate-500">Titulo:</span> {form.title}</p>
            <p><span className="text-slate-500">Fotos:</span> {form.photos.length}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setStep(2)}
              className="flex-1 py-3 border border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
            >
              Atras
            </button>
            <button
              data-testid="mnt-submit-btn"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="flex-1 py-3 bg-[#FFDF00] hover:bg-yellow-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-900 font-semibold rounded-xl transition-colors"
            >
              {submitting ? "Enviando..." : "Enviar solicitud"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
